// Accept-Sage-as-final dialog for a sage_confirmation_mismatch red flag.
//
// Shows the admin exactly what accepting live Sage would change on the order
// (line updates / adds / removes, header fields, subtotal before -> after,
// Sage rows deliberately ignored, blockers), then applies it on confirm.
// Kanban 11438d3f.

import { useEffect, useState } from 'react';
import { Button, Modal } from '../common';
import {
  adminService,
  type AcceptSagePreview,
  type AcceptSageResult,
} from '../../services/admin';
import styles from './AcceptSageDialog.module.css';

interface AcceptSageDialogProps {
  flagId: string | null;
  onClose: () => void;
  /** Called after a successful accept so the caller can refresh its lists. */
  onAccepted: (result: AcceptSageResult) => void | Promise<void>;
}

function errorDetail(error: unknown): string {
  const maybe = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = maybe?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (maybe?.message) return maybe.message;
  return 'Request failed';
}

function money(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fieldLabel(field: string): string {
  switch (field) {
    case 'item_code': return 'Item';
    case 'item_description': return 'Description';
    case 'quantity': return 'Qty';
    case 'unit_price': return 'Unit price';
    case 'po_number': return 'PO number';
    case 'requested_ship_date': return 'Required by';
    case 'warehouse_code': return 'Warehouse';
    case 'udf_jobsite': return 'Jobsite';
    case 'ship_to_address.ship_to_code': return 'Ship-to code';
    default: return field.replace(/_/g, ' ');
  }
}

function valueText(field: string, value: string | null): string {
  if (value === null || value === '') return '—';
  return field === 'unit_price' ? money(value) : value;
}

export function AcceptSageDialog({ flagId, onClose, onAccepted }: AcceptSageDialogProps) {
  const [preview, setPreview] = useState<AcceptSagePreview | null>(null);
  const [result, setResult] = useState<AcceptSageResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!flagId) return;
    let cancelled = false;
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(true);
    adminService
      .previewAcceptSage(flagId)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((e: unknown) => { if (!cancelled) setError(errorDetail(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flagId]);

  const handleAccept = async () => {
    if (!flagId) return;
    setApplying(true);
    setError(null);
    try {
      const r = await adminService.acceptSage(flagId);
      setResult(r);
      await onAccepted(r);
    } catch (e: unknown) {
      setError(errorDetail(e));
    } finally {
      setApplying(false);
    }
  };

  const changeCount = preview
    ? preview.line_updates.length + preview.line_adds.length + preview.line_removes.length + preview.header_changes.length
    : 0;

  return (
    <Modal isOpen={flagId !== null} onClose={onClose} title="Accept Sage as final" size="lg">
      <div className={styles.body} data-testid="accept-sage-dialog">
        {loading && <p className={styles.muted}>Reading the Sage sales order…</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}

        {result && (
          <div className={styles.success} role="status">
            <strong>Accepted.</strong> Sage SO {result.preview.sales_order_no} is now the order of record:
            {' '}{result.lines_updated} line(s) updated, {result.lines_added} added, {result.lines_removed} removed,
            {' '}{result.header_fields_changed} header field(s) changed. New subtotal {money(result.subtotal)}.
            {' '}{result.resolved_flag_count} flag(s) resolved by {result.accepted_by}.
          </div>
        )}

        {preview && !result && (
          <>
            <p className={styles.intro}>
              Sage SO <strong>{preview.sales_order_no}</strong>, read from{' '}
              <span className={styles.sourceBadge}>
                {preview.source === 'live' ? 'live Sage' : 'the Postgres mirror'}
              </span>
              {' '}at {new Date(preview.captured_at).toLocaleString()}. Our order record will be rewritten to match;
              every change is audited as <code>sage_reconcile</code> and the original submission stays on file.
            </p>

            {preview.blockers.length > 0 && (
              <div className={styles.blockers} role="alert">
                <strong>Cannot accept yet</strong>
                <ul>
                  {preview.blockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            )}

            {preview.is_noop && preview.blockers.length === 0 && (
              <p className={styles.muted}>
                Our order already matches Sage. Nothing to accept; resolve the flag instead.
              </p>
            )}

            {preview.line_updates.length > 0 && (
              <section className={styles.section}>
                <h4>Lines changed</h4>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Line</th><th>Field</th><th>Ours</th><th>Sage</th></tr>
                  </thead>
                  <tbody>
                    {preview.line_updates.flatMap((u) =>
                      u.changes.map((c, i) => (
                        <tr key={`${u.line_item_id}-${c.field}`}>
                          <td>{i === 0 ? `${u.position} · ${u.item_code}` : ''}</td>
                          <td>{fieldLabel(c.field)}</td>
                          <td className={styles.old}>{valueText(c.field, c.old)}</td>
                          <td className={styles.new}>{valueText(c.field, c.new)}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </section>
            )}

            {preview.line_adds.length > 0 && (
              <section className={styles.section}>
                <h4>Lines added from Sage</h4>
                <ul className={styles.list}>
                  {preview.line_adds.map((a) => (
                    <li key={`${a.item_code}-${a.sage_line_key ?? ''}`}>
                      <strong>{a.item_code}</strong> {a.item_description} × {a.quantity} @ {money(a.unit_price)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {preview.line_removes.length > 0 && (
              <section className={styles.section}>
                <h4>Lines removed (not on the Sage order)</h4>
                <ul className={styles.list}>
                  {preview.line_removes.map((r) => (
                    <li key={r.line_item_id}>
                      line {r.position} · <strong>{r.item_code}</strong> × {r.quantity ?? '—'} @ {money(r.unit_price)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {preview.header_changes.length > 0 && (
              <section className={styles.section}>
                <h4>Header</h4>
                <table className={styles.table}>
                  <thead><tr><th>Field</th><th>Ours</th><th>Sage</th></tr></thead>
                  <tbody>
                    {preview.header_changes.map((c) => (
                      <tr key={c.field}>
                        <td>{fieldLabel(c.field)}</td>
                        <td className={styles.old}>{valueText(c.field, c.old)}</td>
                        <td className={styles.new}>{valueText(c.field, c.new)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {changeCount > 0 && (
              <p className={styles.totals}>
                Subtotal {money(preview.subtotal_before)} → <strong>{money(preview.subtotal_after)}</strong>
              </p>
            )}

            {preview.skipped_sage_rows.length > 0 && (
              <details className={styles.skipped}>
                <summary>{preview.skipped_sage_rows.length} Sage row(s) ignored (memo / kit components)</summary>
                <ul>
                  {preview.skipped_sage_rows.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </details>
            )}
          </>
        )}

        <div className={styles.actions}>
          <Button variant="outline" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
          {!result && (
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={!preview || !preview.can_apply || applying || loading}
            >
              {applying ? 'Accepting…' : 'Accept Sage values'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
